import { useQueryClient } from "@tanstack/react-query"
import { useTimeCapsuleBySlug } from "api/ApiHooks"
import { LoversApi } from "api/LoversApi"
import { AxiosError } from "axios"
import { DatePicker } from "components/DatePicker"
import { TimeCapsuleCollaboratorsForm } from "components/TimeCapsuleCollaboratorsForm"
import { Button, Input, Label, TextField } from "components/ui"
import dayjs from "dayjs"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { formatTimeDiff } from "utils/FormatTimeDiff"

const TimeCapsuleSettingsLoadingView = () => {
  return (
    <main className="py-10 lg:pl-72">
      <div className="px-4 sm:px-6 lg:px-8">
        <p>Loading...</p>
      </div>
    </main>
  )
}

const TimeCapsuleSettingsForm = (props: {
  name: string
  opensAt?: Date
  error?: string
  isLoading: boolean
  onSave: (name: string, opensAt: Date) => void
}) => {
  const [name, setName] = useState(props.name)
  const [opensAt, setOpensAt] = useState(props.opensAt || dayjs().add(1, "day").toDate())

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    props.onSave(name, opensAt)
  }

  return (
    <div className="pb-10">
      <div className="px-4 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-12">
            <div className="pb-12">
              <h2 className="font-semibold text-lg text-gray-900 dark:text-white">
                {props.name} settings
              </h2>
              <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-300">
                You are updating the settings for this time capsule.
              </p>

              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <div className="sm:max-w-md">
                    <TextField
                      label="Time capsule name"
                      id="name"
                      name="name"
                      type="text"
                      value={name || props.name || ""}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="For my 30th birthday"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <Label>When to open</Label>
                  <div className="mt-2">
                    <div className="sm:max-w-md">
                      <DatePicker
                        defaultDate={
                          opensAt ||
                          (props.opensAt ? dayjs(props.opensAt).toDate() : undefined)
                        }
                        onDateSelected={setOpensAt}
                        onlyFutureDates
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-x-6">
            {name && opensAt ? (
              <p className="block text-sm/6 font-medium text-gray-900 dark:text-white">{`${name} will open in ${formatTimeDiff(opensAt.toISOString())}`}</p>
            ) : (
              <div className="hidden lg:block" />
            )}
            <div>
              {props.error && (
                <p className="text-sm/6 text-red-500 text-right mb-3">{props.error}</p>
              )}

              <Button
                type="submit"
                isLoading={props.isLoading}
                disabled={!name || !opensAt}
                className="w-full lg:w-auto"
              >
                {props.isLoading ? "Updating time capsule..." : "Update time capsule"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

const DeleteTimeCapsuleForm = (props: { timeCapsuleId: string; name: string }) => {
  const [confirmName, setConfirmName] = useState("")
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(undefined)
    setIsDeleting(true)

    try {
      await LoversApi().timeCapsule.deleteTimeCapsule(props.timeCapsuleId)
      queryClient.invalidateQueries({ queryKey: ["useSelfTimeCapsules"] })
      navigate("/dashboard")
    } catch (err: unknown | AxiosError) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.errorMessage || "Failed to delete time capsule")
      } else {
        setError("Failed to delete time capsule")
      }
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="border-t border-gray-900/10 dark:border-white/10 pt-12 pb-2">
      <div className="space-y-8 p-4 rounded-lg shadow-md border-2 border-red-600 bg-red-50 dark:bg-red-950/20 sm:px-6">
        <div>
          <h1 className="text-3xl font-semibold text-red-500 mb-3">Danger zone</h1>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Delete time capsule
          </h2>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">
            This action cannot be undone. To confirm deletion, please type the time
            capsule name: <span className="font-semibold">{props.name}</span>
          </p>
        </div>

        <form onSubmit={handleDelete} className="space-y-6">
          <div>
            <Input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Enter time capsule name"
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <Button
            type="submit"
            variant="danger"
            isLoading={isDeleting}
            disabled={confirmName !== props.name}
          >
            {isDeleting ? "Deleting..." : "Delete time capsule"}
          </Button>
        </form>
      </div>
    </div>
  )
}

export const TimeCapsuleSettingsPage = () => {
  const { slug } = useParams()
  const { data, isLoading } = useTimeCapsuleBySlug(slug || "")

  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleSave = async (name: string, opensAt: Date) => {
    setError(undefined)
    setIsSaving(true)

    console.log(name, opensAt, data?.id)

    try {
      if (!name || !opensAt || !data?.id) {
        throw new Error("Name and opens at date are required")
      }

      const res = await LoversApi().timeCapsule.updateTimeCapsule(data.id, {
        name,
        opensAt: opensAt.toISOString()
      })

      queryClient.invalidateQueries({ queryKey: ["useTimeCapsuleBySlug", res.data.slug] })
      queryClient.invalidateQueries({ queryKey: ["useSelfTimeCapsules"] })
      navigate(`/time-capsules/${res.data.slug}`)
    } catch (err: unknown | AxiosError) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.errorMessage || "Failed to update time capsule")
      } else {
        setError("Failed to update time capsule")
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      {isLoading || !data ? (
        <TimeCapsuleSettingsLoadingView />
      ) : (
        <div>
          <TimeCapsuleSettingsForm
            name={data.name}
            opensAt={data.opensAt ? dayjs(data.opensAt).toDate() : undefined}
            onSave={handleSave}
            error={error}
            isLoading={isSaving}
          />
          <TimeCapsuleCollaboratorsForm
            timeCapsuleId={data.id}
            onSave={() => console.log()}
          />
          <DeleteTimeCapsuleForm timeCapsuleId={data.id} name={data.name} />
        </div>
      )}
    </>
  )
}
